import React from "react";

import { css } from "@emotion/css";
import Typography from "@mui/material/Typography";
import { toast, ToastContainer, ToastContainerProps } from "react-toastify";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";

const TOAST_WIDTH_PX = 320;
const TOAST_MIN_HEIGHT_PX = 64;
const TOAST_MAX_HEIGHT_PX = 800;

export const defaultToastOptions: ToastContainerProps = {
  pauseOnHover: true,
  pauseOnFocusLoss: false,
  closeOnClick: true,
  autoClose: 3000,
  position: "top-right",
  draggable: true,
  rtl: false,
  role: "alert",
  stacked: true,
};

export const CustomToastContainer: React.FC = () => {
  const styles = useStyles(makeStyles);
  return <ToastContainer {...defaultToastOptions} className={styles} />;
};

const makeStyles = (theme: Theme) => css`
  --toastify-color-light: ${theme.palette.common.white};
  --toastify-color-dark: ${theme.palette.primary.main};
  --toastify-color-info: ${theme.palette.info.light};
  --toastify-color-success: ${theme.palette.success.light};
  --toastify-color-warning: ${theme.palette.warning.light};
  --toastify-color-error: ${theme.palette.error.light};
  --toastify-color-transparent: ${theme.palette.common.white}B3;

  --toastify-icon-color-info: var(--toastify-color-info);
  --toastify-icon-color-success: var(--toastify-color-success);
  --toastify-icon-color-warning: var(--toastify-color-warning);
  --toastify-icon-color-error: var(--toastify-color-error);

  --toastify-toast-width: ${TOAST_WIDTH_PX}px;
  --toastify-toast-background: var(--toastify-color-light);
  --toastify-toast-min-height: ${TOAST_MIN_HEIGHT_PX}px;
  --toastify-toast-max-height: ${TOAST_MAX_HEIGHT_PX}px;
  --toastify-font-family: ${theme.typography.fontFamily};
  --toastify-z-index: ${theme.zIndex.snackbar};

  --toastify-text-color-light: ${theme.palette.text.primary};
  --toastify-text-color-dark: var(--toastify-color-light);

  --toastify-spinner-color: var(--toastify-color-dark);
  --toastify-spinner-color-empty-area: ${theme.palette.grey["100"]};
`;

export const SimpleTextToast: React.FC<{ text: string }> = ({ text }) => <Typography variant="body">{text}</Typography>;

export const TextToastWithSubtext: React.FC<{
  text: string;
  subtext: string;
}> = ({ text, subtext }) => (
  <div
    className={css`
      display: flex;
      flex-direction: column;
    `}
  >
    <Typography variant="body">{text}</Typography>
    <Typography variant="caption">{subtext}</Typography>
  </div>
);

export const TextToastSwitch: React.FC<{ text: string; subtext?: string }> = ({ text, subtext }) => {
  switch (true) {
    case subtext !== undefined:
      return <TextToastWithSubtext text={text} subtext={subtext} />;
    default:
      return <SimpleTextToast text={text} />;
  }
};

type ToastOptions = {
  onClick?: () => void;
};

export const TextToast = {
  info: (text: string, subtext?: string, options?: ToastOptions) =>
    toast.info(<TextToastSwitch text={text} subtext={subtext} />, options),
  success: (text: string, subtext?: string, options?: ToastOptions) =>
    toast.success(<TextToastSwitch text={text} subtext={subtext} />, options),
  warning: (text: string, subtext?: string, options?: ToastOptions) =>
    toast.warning(<TextToastSwitch text={text} subtext={subtext} />, options),
  error: (text: string, subtext?: string, options?: ToastOptions) =>
    toast.error(<TextToastSwitch text={text} subtext={subtext} />, options),
};
